<template>
  <div>
    <template v-if="loading">
      <SkeletonLoader v-for="n in 6" :key="n" width="100%" height="171px" />
    </template>

    <template v-else-if="isTalent || isManager || isAdmin || isInvestor">
      <div class="overflow-x-auto text-grayDark">
        <div
          v-if="filteredProjects.length === 0"
          class="text-center py-16 text-gray-500"
        >
          No project available.
        </div>
        <div v-else>
          <div class="hidden md:block overflow-x-auto">
            <table class="min-w-full divide-y">
              <thead class="text-[12px] text-primaryDarkBg">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left tracking-wider">
                    Name
                  </th>
                  <th scope="col" class="px-6 py-3 text-left tracking-wider">
                    Progress
                  </th>
                  <th scope="col" class="px-6 py-3 text-left tracking-wider">
                    Status
                  </th>
                  <th
                    v-if="!isInvestor"
                    scope="col"
                    class="px-6 py-3 text-left tracking-wider"
                  >
                    Members
                  </th>
                  <th
                    v-if="!isInvestor"
                    scope="col"
                    class="px-6 py-3 text-left tracking-wider"
                  >
                    Completion date
                  </th>
                  <th
                    v-if="!isInvestor"
                    scope="col"
                    class="px-6 py-3 text-left tracking-wider"
                  >
                    Tasks completed
                  </th>
                  <th scope="col" class="px-6 py-3 text-left tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y text-bodyText">
                <tr
                  v-for="project in filteredProjects"
                  :key="project.project_id"
                  class="mb-2 text-[13px]"
                >
                  <td
                    @click="goToProject(project.project_id)"
                    class="cursor-pointer px-4 py-4 font-semibold whitespace-nowrap"
                  >
                    {{ project.project_name }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div
                        class="relative w-[124px] h-[9px] bg-[#F3F4F6] rounded-full overflow-hidden"
                      >
                        <!-- Inner progress bar -->
                        <div
                          class="absolute top-0 left-0 h-full bg-primaryDark rounded-full"
                          :style="{ width: `${project.progress}%` }"
                        ></div>
                      </div>
                      <span class="ml-2 text-[12px] font-medium"
                        >{{ project.progress }}%</span
                      >
                    </div>
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap">
                    <div
                      v-if="project.status === 'Active'"
                      class="flex items-center gap-2 py-1 px-2 rounded-full bg-[#EBF1FF] text-primaryDarkBg"
                    >
                      <div class="w-2 h-2 bg-primaryDarkBg rounded-full"></div>
                      {{ project.status }}
                    </div>
                    <div
                      v-else-if="project.status === 'Not Started'"
                      class="flex items-center gap-2 py-1 px-2 rounded-full bg-[#FFF5E6] text-pendingStatus"
                    >
                      <div class="w-2 h-2 bg-pendingStatus rounded-full"></div>
                      {{ project.status }}
                    </div>
                    <div
                      v-else-if="project.status === 'Completed'"
                      class="flex items-center gap-2 py-1 px-2 rounded-full bg-[#EBFFEB] text-completedStatus"
                    >
                      <div
                        class="w-2 h-2 bg-completedStatus rounded-full"
                      ></div>
                      {{ project.status }}
                    </div>
                    <div
                      v-else
                      class="flex items-center gap-2 py-1 px-2 rounded-full bg-[#EBF1FF] text-primaryDarkBg"
                    >
                      <div class="w-2 h-2 bg-primaryDarkBg rounded-full"></div>
                      {{ project.status }}
                    </div>
                  </td>

                  <td v-if="!isInvestor" class="px-4 py-4 whitespace-nowrap">
                    <div class="flex -space-x-2 rounded-full">
                      <UserAvatar
                        v-for="(member, index) in project.members"
                        :key="index"
                        :fullName="member"
                      />
                    </div>
                  </td>

                  <td v-if="!isInvestor" class="px-4 py-4 whitespace-nowrap">
                    <div
                      class="flex items-center rounded-[8px] text-grayDark font-medium gap-2"
                    >
                      <img
                        src="@/assets/projects/task-square.svg"
                        alt="team"
                        class="h-4 overlap"
                      />
                      {{ formatDate(project.end_date) }}
                    </div>
                  </td>

                  <td v-if="!isInvestor" class="px-4 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-2">
                      <span
                        >{{ project.completed_task_count || 0 }} /
                        {{ project.task_count || 0 }}</span
                      >
                    </div>
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap relative">
                    <div class="relative">
                      <button
                        @click="toggleDropdown(project.project_id)"
                        class="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <svg class="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z"/>
                        </svg>
                      </button>
                      <div
                        v-if="dropdownOpen === project.project_id"
                        class="absolute right-0 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50"
                        :class="getDropdownPosition(project.project_id)"
                      >
                        <div class="py-1">
                          <button
                            @click="goToProjectAssets(project.project_id)"
                            class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            View assets
                          </button>
                          <button
                            v-if="isAdmin || isManager"
                            @click="openDeleteModal(project.project_id, project.project_name)"
                            class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile Card View -->
          <div class="md:hidden">
            <div
              v-for="project in filteredProjects"
              :key="project.project_id"
              class="bg-white rounded-lg mb-4 p-4 border border-gray-100 text-bodyText"
              @click="goToProject(project.project_id)"
            >
              <div class="grid grid-cols-3 gap-8">
                <!-- Project Name -->
                <div class="space-y-1">
                  <p class="text-[12px]">Name</p>
                  <div class="font-semibold text-[14px]">
                    {{ project.project_name }}
                  </div>
                </div>
                <!-- Status Badge -->
                <div class="space-y-1">
                  <p class="text-[12px]">Status</p>

                  <div
                    v-if="project.status === 'Active'"
                    class="inline-flex items-center gap-2 py-1 px-2 rounded-full bg-[#EBF1FF] text-primaryDarkBg text-xs"
                  >
                    <div class="w-2 h-2 bg-primaryDarkBg rounded-full"></div>
                    {{ project.status }}
                  </div>
                  <div
                    v-else-if="project.status === 'Not Started'"
                    class="inline-flex items-center gap-2 py-1 px-2 rounded-full bg-[#FFF5E6] text-pendingStatus text-xs"
                  >
                    <div class="w-2 h-2 bg-pendingStatus rounded-full"></div>
                    {{ project.status }}
                  </div>
                  <div
                    v-else-if="project.status === 'Completed'"
                    class="inline-flex items-center gap-2 py-1 px-2 rounded-full bg-[#EBFFEB] text-completedStatus text-xs"
                  >
                    <div class="w-2 h-2 bg-completedStatus rounded-full"></div>
                    {{ project.status }}
                  </div>
                  <div
                    v-else
                    class="inline-flex items-center gap-2 py-1 px-2 rounded-full bg-[#EBF1FF] text-primaryDarkBg text-xs"
                  >
                    <div class="w-2 h-2 bg-primaryDarkBg rounded-full"></div>
                    {{ project.status }}
                  </div>
                </div>
                <!-- Tasks completed -->
                <div class="space-y-1">
                  <div class="text-xs text-gray-500">Tasks completed</div>
                  <div class="text-[13px]">
                    {{ project.completed_task_count || 0 }} /
                    {{ project.task_count || 0 }}
                  </div>
                </div>
                <!-- Progress bar -->
                <div class="space-y-1">
                  <p class="text-[12px]">Progress</p>
                  <div class="flex items-center">
                    <div
                      class="relative w-[121px] h-[9px] bg-[#F3F4F6] rounded-full overflow-hidden"
                    >
                      <!-- Inner progress bar -->
                      <div
                        class="absolute top-0 left-0 h-full bg-primaryDark rounded-full"
                        :style="{
                          width: `${
                           project.progress
                          }%`,
                        }"
                      ></div>
                    </div>
                    <span class="ml-2 text-[12px] font-medium">
                      {{
                        project.progress
                      }}%
                    </span>
                  </div>
                </div>

                <!-- Team members -->
                <!-- <div class="col-span-1">
                  <div class="flex -space-x-2">
                    <UserAvatar
                      v-for="(member, index) in project.members.slice(0, 3)"
                      :key="index"
                      :fullName="member.fullName"
                    />
                    <div v-if="project.members.length > 3" class="flex items-center justify-center bg-gray-200 text-gray-600 rounded-full h-6 w-6 text-xs">
                      +{{ project.members.length - 3 }}
                    </div>
                  </div>
                </div> -->

                <!-- Action button -->
                <div class="space-y-1 items-center relative">
                  <p class="text-[12px]">Action</p>
                  <div class="relative">
                    <button
                      @click.stop="toggleDropdown(project.project_id)"
                      class="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <svg class="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z"/>
                      </svg>
                    </button>
                    <div
                      v-if="dropdownOpen === project.project_id"
                      class="absolute right-0 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50"
                      :class="getDropdownPosition(project.project_id)"
                    >
                      <div class="py-1">
                        <button
                          @click.stop="goToProjectAssets(project.project_id)"
                          class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          View assets
                        </button>
                        <button
                          v-if="isAdmin || isManager"
                          @click.stop="openDeleteModal(project.project_id, project.project_name)"
                          class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Completion date -->
                <div class="space-y-1">
                  <p class="text-[12px]">Completion Date</p>
                  <div
                    class="mt-3 text-xs flex items-center gap-1 text-gray-500"
                  >
                    <img
                      src="@/assets/projects/task-square.svg"
                      alt="team"
                      class="h-4 overlap"
                    />
                    <span>{{
                      formatDate(
                         project.end_date
                      )
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <PaginationComponent
            :currentPage="currentPage"
            :totalPages="totalPages"
            @changePage="handlePageChange"
          />
        </div>
      </div>
    </template>

    <!-- Delete Project Modal -->
    <DeleteProjectModal
      :isOpen="deleteModalOpen"
      :projectId="selectedProject.id"
      :projectName="selectedProject.name"
      @close="closeDeleteModal"
      @confirm-delete="confirmDeleteProject"
    />
  </div>
</template>

<script>
import apiClient from "@/axios";
import UserAvatar from "../auth/UserAvatar.vue";
import PaginationComponent from "../PaginationComponent.vue";
import DeleteProjectModal from "../modals/DeleteProjectModal.vue";

export default {
  name: "ProjectList",
  components: {
    PaginationComponent,
    UserAvatar,
    DeleteProjectModal,
  },
  data() {
    return {
      viewMode: "list",
      localProjects: [], // local data instead of mutating props
      filteredProjects: [],
      loading: true,
      managedProjects: [],
      pageSize: 100,
      dropdownOpen: null,
      deleteModalOpen: false,
      selectedProject: {
        id: null,
        name: ''
      }
    };
  },
  props: {
    projects: {
      type: Array,
      default: () => [],
    },
    selectedStatus: {
      type: String,
      default: "All",
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
  },
  watch: {
    projects: {
      handler(newProjects) {
        this.localProjects = Array.isArray(newProjects) ? newProjects : [];
        this.filterProjects(this.selectedStatus);

      },
      immediate: true,
    },
    selectedStatus: {
      handler(newStatus) {
        this.localSelectedStatus = newStatus;
        this.filterProjects(newStatus);
      },
      immediate: true,
    },
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
    isInvestor() {
      return this.$store.getters.isInvestor;
    },
  },
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      // Close dropdown if clicking outside of any dropdown
      if (!event.target.closest('.relative')) {
        this.dropdownOpen = null;
      }
    },
    setViewMode(mode) {
      this.viewMode = mode;
    },
    async fetchProjects() {
      try {
        this.filterProjects("All");
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchProjectTasks(projectId) {
      try {
        if (this.isAdmin || this.isManager || this.isTalent) {
          const response = await apiClient.get(
            `/web/projects/${projectId}/tasks`
          );
          const tasks = response.data.data;

          const completedTasks = tasks.filter(
            (task) => task.task_status === "Completed"
          ).length;
          const totalTasks = tasks.length;

          return { completedTasks, totalTasks };
        }
      } catch (error) {
        console.error(`Error fetching tasks for project ${projectId}:`, error);
        return { completedTasks: 0, totalTasks: 0 };
      }
    },
    handlePageChange(newPage) {
      if (newPage > 0 && newPage <= this.totalPages) {
        this.$emit("page-change", newPage);
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = date.getMonth() + 1; // Months are zero-based
      const day = date.getDate();
      return `${month}/${day}`;
    },
    goToProject(projectId) {
      this.$router.push({ name: "ProjectDetails", params: { projectId } });
    },
    goToProjectAssets(projectId) {
      this.$router.push({ name: "ProjectAssets", params: { projectId } });
    },
    filterProjects(status) {
      if (status === "All") {
        this.filteredProjects = this.localProjects;
        console.log("All Filtered projects:", this.filteredProjects);
      } else {
        this.filteredProjects = this.localProjects.filter(
          (project) => (project.project_status || project.status) === status
        );
        console.log("Filtered projects by status:", status, this.filteredProjects);
      }
    },
    toggleDropdown(projectId) {
      this.dropdownOpen = this.dropdownOpen === projectId ? null : projectId;
    },
    getDropdownPosition(projectId) {
      const projectIndex = this.filteredProjects.findIndex(p => p.project_id === projectId);
      const totalProjects = this.filteredProjects.length;
      
      if (projectIndex === totalProjects - 1 || totalProjects === 1) {
        return 'bottom-full mb-2';
      } else {
        return 'top-full mt-2';
      }
    },
    openDeleteModal(projectId, projectName) {
      this.selectedProject = {
        id: projectId,
        name: projectName
      };
      this.deleteModalOpen = true;
      this.dropdownOpen = null; // Close dropdown when modal opens
    },
    closeDeleteModal() {
      this.deleteModalOpen = false;
      this.selectedProject = {
        id: null,
        name: ''
      };
    },
    async confirmDeleteProject(projectId) {
      try {
        await apiClient.delete(`/web/projects/${projectId}`);
        
        this.localProjects = this.localProjects.filter(project => project.project_id !== projectId);
        this.filteredProjects = this.filteredProjects.filter(project => project.project_id !== projectId);
        
        this.closeDeleteModal();
        
        this.$emit('project-deleted', projectId);
        
        console.log('Project deleted successfully');
        
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    },
    
  },
  created() {
    this.fetchProjects();  
  }
};
</script>
